"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function BlogFeedConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border/50 shadow-sm bg-card">
            <div className="aspect-[16/10] bg-muted animate-pulse" />
            <CardContent className="p-8 space-y-4">
              <div className="h-3 w-20 bg-muted animate-pulse rounded-lg" />
              <div className="h-7 w-full bg-muted animate-pulse rounded-lg" />
              <div className="space-y-2">
                <div className="h-4 w-full bg-muted animate-pulse rounded-lg" />
                <div className="h-4 w-2/3 bg-muted animate-pulse rounded-lg" />
              </div>
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-20 bg-accent/30 rounded-2xl border border-accent">
        <p className="font-default text-foreground font-medium">인사이트 리포트를 불러오는 데 실패했습니다. 잠시 후 다시 시도해주세요.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-20 bg-muted/50 rounded-2xl border border-border">
        <p className="font-default text-muted-foreground">현재 등록된 최신 인사이트가 없습니다.</p>
      </div>;
  }
  return <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {posts.map((post, i) => {
        const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "최신 소식";
        const postDate = post.date ? new Date(post.date).toLocaleDateString('ko-KR', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        }) : '';
        return <Card key={post.id} data-index={i} className="overflow-hidden border-border/50 shadow-sm hover:shadow-xl transition-all duration-500 group flex flex-col bg-card rounded-xl border">
              <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center bg-secondary/5 text-secondary/40">
                  </div>}
                <div className="absolute inset-0 bg-linear-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                {postDate && <div className="absolute top-4 right-4 bg-background/80 backdrop-blur-md text-foreground px-3 py-1.5 rounded-lg shadow-sm flex items-center gap-1.5 border border-border/50">
                    <SafeHydrate><Calendar className="size-3 text-primary" /></SafeHydrate>
                    <span className="text-[10px] font-bold tracking-wider font-default uppercase">{postDate}</span>
                  </div>}
              </div>
              <CardContent className="p-8 flex flex-col flex-grow gap-5">
                <div className="flex items-center gap-2">
                  <div className="h-px w-4 bg-primary" />
                  <span className="text-primary font-bold text-xs md:text-sm lg:text-sm uppercase tracking-[0.2em] font-default">
                    {category}
                  </span>
                </div>
                <h3 className="font-default text-xl font-bold text-foreground line-clamp-2 group-hover:text-primary transition-colors leading-[1.3] tracking-tight">
                  {post.title}
                </h3>
                <div className="text-muted-foreground text-sm md:text-base lg:text-base line-clamp-3 flex-grow font-default leading-relaxed opacity-80" dangerouslySetInnerHTML={{
              __html: post.excerpt || ''
            }} />
                <div className="pt-4 mt-auto border-t border-border/50">
                  <Link to={post.link || "#"} className="inline-flex items-center gap-2 text-sm font-bold text-foreground group/link transition-colors font-default">
                    <span className="relative">
                      리포트 읽기
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover/link:w-full" />
                    </span>
                    <ArrowRight className="size-4 transition-transform group-hover/link:translate-x-1 text-primary" />
                  </Link>
                </div>
              </CardContent>
            </Card>;
      })}
      </div>
      <div className="mt-16 flex justify-center">
        <Link to="/LatestInsightsPage" className="inline-flex items-center gap-3 px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold hover:bg-primary/90 transition-all duration-300 font-default shadow-lg hover:shadow-primary/20 hover:-translate-y-1">
          전체 인사이트 보기
          <ArrowRight className="size-5" />
        </Link>
      </div>
    </>;
}
export default function BlogUpdatesFeed() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="248898" id="최신-인사이트-리포트" className="bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/5 rounded-lg blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/5 rounded-lg blur-3xl pointer-events-none" />
        
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24 relative">
          <div className="inline-block px-4 py-1.5 mb-6 rounded-lg bg-primary/10 border border-primary/20">
            <span className="text-primary text-xs font-bold tracking-[0.2em] uppercase">Knowledge Hub</span>
          </div>
          <h2 className="font-default text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tighter leading-tight">
            최신 인사이트 리포트
          </h2>
          <div className="h-1.5 w-20 bg-primary mx-auto mb-8 rounded-lg" />
          <p className="text-muted-foreground text-lg md:text-xl lg:text-2xl font-default max-w-2xl mx-auto leading-relaxed">
            건양사이버대학교의 입시 정보, 학사 소식,<br className="md:block" /> 재학생 성공 사례를 확인하세요.
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <BlogFeedConsumer />
        </PostsProvider>
      </div>
    </section>;
}